import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MapPin, BedDouble, Bath, Users, Pencil, Trash2, X, Loader2, Upload, ImageIcon } from "lucide-react";

const FALLBACK_IMG = "https://images.pexels.com/photos/11400444/pexels-photo-11400444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const empty = { name: "", location: "", description: "", bedrooms: 1, bathrooms: 1, max_guests: 2, price_per_night: 0, amenities: [], images: [], status: "active" };

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1A365D] focus:border-transparent transition-all outline-none";

export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...(Array.isArray(f.images) ? f.images : []), ...data.urls] }));
      toast.success(`${data.urls.length} photo${data.urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const load = () => {
    setLoading(true);
    api.get("/properties").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, amenities: p.amenities || [], images: p.images || [] });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      bedrooms: Number(form.bedrooms),
      bathrooms: Number(form.bathrooms),
      max_guests: Number(form.max_guests),
      price_per_night: Number(form.price_per_night),
      amenities: typeof form.amenities === "string" ? form.amenities.split(",").map((s) => s.trim()).filter(Boolean) : form.amenities,
      images: typeof form.images === "string" ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : form.images,
    };
    try {
      if (editing) await api.put(`/properties/${editing.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(editing ? "Property updated" : "Property added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This removes its bookings too.`)) return;
    await api.delete(`/properties/${p.id}`);
    toast.success("Property deleted");
    load();
  };

  const statusColor = { active: "#81B29A", maintenance: "#E07A5F", inactive: "#9CA3AF" };

  return (
    <div data-testid="properties-page" className="max-w-6xl">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Portfolio</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-medium">Properties</h1>
        </div>
        <button data-testid="add-property-button" onClick={openNew} className="bg-[#1A365D] text-white hover:bg-[#2A4A7F] rounded-full px-5 py-2.5 font-medium transition-colors flex items-center gap-2">
          <Plus size={18} /> Add Property
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center pt-20"><Loader2 className="animate-spin text-[#1A365D]" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-gray-400">No properties yet. Add your first vacation home.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((p) => (
            <div key={p.id} data-testid={`property-card-${p.id}`} className="bg-white border border-black/5 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={imgUrl(p.images?.[0]) || FALLBACK_IMG} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-white capitalize" style={{ background: statusColor[p.status] }}>
                  {p.status}
                </span>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-2xl font-medium leading-tight">{p.name}</h3>
                  <span className="font-mono text-sm text-[#E07A5F] whitespace-nowrap">${p.price_per_night}/nt</span>
                </div>
                <p className="text-sm text-gray-500 flex items-center gap-1 mt-1"><MapPin size={14} /> {p.location}</p>
                <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1"><BedDouble size={16} /> {p.bedrooms}</span>
                  <span className="flex items-center gap-1"><Bath size={16} /> {p.bathrooms}</span>
                  <span className="flex items-center gap-1"><Users size={16} /> {p.max_guests}</span>
                </div>
                <div className="flex gap-2 mt-5">
                  <button data-testid={`edit-property-${p.id}`} onClick={() => openEdit(p)} className="flex-1 bg-[#F4F1EA] text-[#1A365D] hover:bg-[#E3D5CA] rounded-full px-4 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors">
                    <Pencil size={15} /> Edit
                  </button>
                  <button data-testid={`delete-property-${p.id}`} onClick={() => remove(p)} className="px-3 py-2 rounded-full text-gray-400 hover:text-white hover:bg-[#EF4444] transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A365D]/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div data-testid="property-dialog" className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 shadow-xl animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl font-medium">{editing ? "Edit Property" : "New Property"}</h2>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><X size={22} /></button>
            </div>
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Field label="Name"><input data-testid="property-name-input" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field></div>
              <div className="sm:col-span-2"><Field label="Location"><input data-testid="property-location-input" required className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Miami Beach, FL" /></Field></div>
              <Field label="Bedrooms"><input type="number" min="0" className={inputCls} value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} /></Field>
              <Field label="Bathrooms"><input type="number" min="0" className={inputCls} value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} /></Field>
              <Field label="Max Guests"><input type="number" min="1" className={inputCls} value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: e.target.value })} /></Field>
              <Field label="Price / night ($)"><input data-testid="property-price-input" type="number" min="0" step="0.01" className={inputCls} value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} /></Field>
              <Field label="Status">
                <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="inactive">Inactive</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Villa Photos">
                  <div className="flex flex-wrap gap-3 mb-3">
                    {(Array.isArray(form.images) ? form.images : []).map((img, idx) => (
                      <div key={idx} className="relative w-24 h-24 rounded-xl overflow-hidden border border-black/5 group">
                        <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                        <button type="button" data-testid={`remove-image-${idx}`} onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-black/60 hover:bg-[#EF4444] text-white rounded-full p-1 transition-colors">
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                    <button type="button" data-testid="upload-photo-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#1A365D] text-gray-400 hover:text-[#1A365D] flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-60">
                      {uploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />}
                      <span className="text-[11px] font-medium">{uploading ? "Uploading" : "Upload"}</span>
                    </button>
                  </div>
                  <input ref={fileRef} data-testid="photo-file-input" type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
                  <p className="text-xs text-gray-400 flex items-center gap-1"><ImageIcon size={12} /> Upload photos from your phone or computer (JPG, PNG). The first photo is used as the cover.</p>
                </Field>
              </div>
              <div className="sm:col-span-2"><Field label="Amenities (comma separated)"><input className={inputCls} value={Array.isArray(form.amenities) ? form.amenities.join(", ") : form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} placeholder="Pool, WiFi, Beach access" /></Field></div>
              <div className="sm:col-span-2"><Field label="Description"><textarea rows="3" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
              <div className="sm:col-span-2 flex justify-end gap-3 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="bg-[#F4F1EA] text-[#1A365D] hover:bg-[#E3D5CA] rounded-full px-6 py-2.5 font-medium transition-colors">Cancel</button>
                <button data-testid="save-property-button" type="submit" disabled={saving} className="bg-[#1A365D] text-white hover:bg-[#2A4A7F] rounded-full px-6 py-2.5 font-medium transition-colors flex items-center gap-2 disabled:opacity-60">
                  {saving && <Loader2 className="animate-spin" size={16} />} Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
